// imageCompressionUtil.js
//
// Downscales and re-encodes images in the browser BEFORE they are stored.
//
// Every image in this app is kept as a base64 data URL inside the Realtime
// Database (payment receipts on booking records, event photos, and the logo /
// gallery / landing / about / QR images inside `config`). RTDB bills download
// by the byte with no CDN and no browser cache, and base64 adds ~33% on top of
// the raw file — so an untouched 800 KB phone photo costs ~1.07 MB of download
// to every client that reads the node containing it, on every read.
//
// A 4000x3000 phone photo is also far larger than anything this UI displays.
// Re-encoding to a sensible max dimension as WebP typically cuts it 10-20x with
// no visible difference at the sizes it's actually rendered at.
//
// All work happens on the client via <canvas>, so there is no server component
// and no extra network cost to compressing.

/**
 * Compression presets, chosen per image role.
 *
 * `maxDimension` bounds the longest edge (aspect ratio is always preserved).
 * `quality` is the WebP/JPEG encoder quality, 0-1.
 * `maxKB` is a hard ceiling on the RESULT; exceeding it is an error rather
 * than something silently stored, so an oversized image can't quietly land in
 * the database the way it could before.
 */
export const IMAGE_PRESETS = {
  // Landing hero, gallery, about, per-sport images. Displayed large, but never
  // larger than a full-width desktop image.
  PHOTO: { maxDimension: 1600, quality: 0.8, maxKB: 300, label: 'photo' },
  // Club logo — rendered in a header at roughly 40-64px, and in the loading
  // spinner. 512 leaves generous headroom for retina.
  LOGO: { maxDimension: 512, quality: 0.85, maxKB: 60, label: 'logo' },
  // Payment receipts. An admin has to READ these to verify a payment, so this
  // keeps more detail than a decorative photo would need.
  RECEIPT: { maxDimension: 1400, quality: 0.75, maxKB: 250, label: 'receipt' },
  // Payment QR codes. Small, but must stay crisp enough for a phone camera to
  // resolve the modules, hence the higher quality at a modest dimension.
  QR: { maxDimension: 800, quality: 0.9, maxKB: 100, label: 'QR code' },
  // Event posters — displayed at card size in the admin list.
  EVENT: { maxDimension: 1400, quality: 0.8, maxKB: 250, label: 'event image' },
};

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

/**
 * An error whose message is safe and useful to show the user directly.
 *
 * Callers wrap uploads in a try/catch that replaces unknown failures with a
 * generic "please try again", which would otherwise swallow the specific,
 * actionable reasons below ("that file isn't an image", "still too large after
 * compression"). They re-throw anything carrying this flag untouched.
 *
 * @param {string} message
 * @returns {Error}
 */
export const userFacingError = (message) => {
  const error = new Error(message);
  error.isUserFacing = true;
  return error;
};

/**
 * Whether the browser can actually encode WebP. Safari gained support in 14;
 * anything older silently returns a PNG data URL from toDataURL('image/webp'),
 * which would be BIGGER than the JPEG we'd otherwise produce — so this is
 * checked rather than assumed.
 * @returns {boolean}
 */
const supportsWebp = () => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    return false;
  }
};

/**
 * Size of a base64 data URL's payload in KB — what the database actually
 * stores, not the decoded image's byte length.
 * @param {string} dataUrl
 * @returns {number}
 */
export const dataUrlSizeKB = (dataUrl) => (dataUrl || '').length / 1024;

/**
 * Loads a File into an <img> so it can be drawn to a canvas.
 * @param {File} file
 * @returns {Promise<HTMLImageElement>}
 */
const loadImage = (file) => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    // Revoked as soon as the bitmap is decoded — these leak for the lifetime
    // of the document otherwise, which matters on the admin config screen
    // where several images can be swapped in one sitting.
    URL.revokeObjectURL(objectUrl);
    resolve(img);
  };
  img.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(userFacingError('That file could not be read as an image. It may be corrupted.'));
  };
  img.src = objectUrl;
});

/**
 * Scales (width, height) so the longest edge is at most maxDimension.
 * Images already smaller than the bound are left alone — upscaling would add
 * bytes without adding detail.
 * @param {number} width
 * @param {number} height
 * @param {number} maxDimension
 * @returns {{width: number, height: number}}
 */
const fitWithin = (width, height, maxDimension) => {
  const longest = Math.max(width, height);
  if (longest <= maxDimension) return { width, height };
  const scale = maxDimension / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

/**
 * Compresses an image file to a base64 data URL suitable for storing in the
 * Realtime Database.
 *
 * If the first encode still exceeds the preset's `maxKB`, quality is stepped
 * down and it retries — a photo of a noisy scene can encode much larger than
 * an average one at the same quality, and failing outright would just push the
 * problem onto the admin.
 *
 * @param {File} file The user-selected image.
 * @param {object} [preset=IMAGE_PRESETS.PHOTO] One of IMAGE_PRESETS.
 * @returns {Promise<{dataUrl: string, sizeKB: number, originalKB: number, width: number, height: number}>}
 * @throws {Error} With a message safe to show the user, for an unsupported
 *   file type, an unreadable file, or an image that can't be brought under
 *   the preset's ceiling.
 */
export const compressImage = async (file, preset = IMAGE_PRESETS.PHOTO) => {
  if (!file) throw userFacingError('No file selected.');
  if (!ACCEPTED_TYPES.includes(file.type)) {
    throw userFacingError('Invalid file type. Please upload a JPEG, PNG or WebP image.');
  }

  const originalKB = file.size / 1024;
  const img = await loadImage(file);
  const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, preset.maxDimension);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw userFacingError('Image compression is not supported in this browser.');

  // Browsers downscale poorly by default; this is the difference between a
  // crisp logo and an aliased one at small sizes.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const mimeType = supportsWebp() ? 'image/webp' : 'image/jpeg';

  // JPEG has no alpha channel, so a transparent PNG logo would encode its
  // transparent pixels as black without this. WebP does keep alpha, and a
  // logo with a transparent background is worth preserving — so the white
  // flatten is applied only on the JPEG fallback path.
  if (mimeType === 'image/jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(img, 0, 0, width, height);

  let quality = preset.quality;
  let dataUrl = canvas.toDataURL(mimeType, quality);

  // Step down until it fits. Floor at 0.4 — below that the artefacts are
  // obvious and a receipt stops being readable.
  while (dataUrlSizeKB(dataUrl) > preset.maxKB && quality > 0.4) {
    quality = Math.round((quality - 0.1) * 10) / 10;
    dataUrl = canvas.toDataURL(mimeType, quality);
  }

  const sizeKB = dataUrlSizeKB(dataUrl);
  if (sizeKB > preset.maxKB) {
    throw userFacingError(
      `This ${preset.label} is still ${Math.round(sizeKB)}KB after compression ` +
      `(limit ${preset.maxKB}KB). Please crop it or choose a simpler image.`
    );
  }

  return { dataUrl, sizeKB, originalKB, width, height };
};

/**
 * compressImage plus a ready-made "1.8MB -> 74KB" line for a success toast.
 * @param {File} file
 * @param {object} [preset]
 * @returns {Promise<{dataUrl: string, sizeKB: number, summary: string}>}
 */
export const compressImageWithSummary = async (file, preset = IMAGE_PRESETS.PHOTO) => {
  const result = await compressImage(file, preset);
  const fmt = (kb) => (kb >= 1024 ? `${(kb / 1024).toFixed(1)}MB` : `${Math.round(kb)}KB`);
  return {
    ...result,
    summary: `${fmt(result.originalKB)} → ${fmt(result.sizeKB)}`,
  };
};
